import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PortfolioForm from './PortfolioForm';
import './PortfolioManager.css';

const PortfolioManager = () => {
  const { t } = useTranslation();
  const [portfolios, setPortfolios] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);

  // Charger les portfolios depuis localStorage
  useEffect(() => {
    const savedPortfolios = localStorage.getItem('portfolios');
    if (savedPortfolios) {
      setPortfolios(JSON.parse(savedPortfolios));
    } else {
      // Initialiser avec quelques exemples si aucune donnée n'existe
      			const defaultPortfolios = [
				{
					id: 1,
					name: "Site E-commerce",
					description: "Développement d'une boutique en ligne moderne avec interface utilisateur moderne, panier d'achat, système de paiement intégré et tableau de bord administrateur.",
					url: "https://example.com",
					category: "siteweb",
					date: "2023",
					image: "/images/realisation-1.png",
					gallery: [
						"/images/realisation-1.png",
						"/images/realisation-2.png",
						"/images/realisation-3.png"
					],
					technologies: ["React", "Node.js", "MongoDB", "Stripe"],
					client: "E-Commerce Store"
				},
				{
					id: 2,
					name: "Identité Visuelle Restaurant",
					description: "Création complète d'une identité visuelle pour un restaurant gastronomique incluant logo, charte graphique, menu design et supports de communication.",
					url: "https://example.com",
					category: "identitevisuelle",
					date: "2023",
					image: "/images/realisation-2.png",
					gallery: [
						"/images/realisation-2.png",
						"/images/realisation-1.png",
						"/images/realisation-4.png"
					],
					technologies: ["Photoshop", "Illustrator", "InDesign"],
					client: "Le Gourmet"
				}
			];
      setPortfolios(defaultPortfolios);
      localStorage.setItem('portfolios', JSON.stringify(defaultPortfolios));
    }
  }, []);

  // Sauvegarder dans localStorage
  const savePortfolios = (newPortfolios) => {
    setPortfolios(newPortfolios);
    localStorage.setItem('portfolios', JSON.stringify(newPortfolios));
  };

  const handleAdd = () => {
    setEditingPortfolio(null);
    setShowForm(true);
  };

  const handleEdit = (portfolio) => {
    setEditingPortfolio(portfolio);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm(t('common.delete') + ' ?')) {
      const newPortfolios = portfolios.filter(p => p.id !== id);
      savePortfolios(newPortfolios);
    }
  };

  const handleSave = (portfolioData) => {
    if (editingPortfolio) {
      // Modification
      const newPortfolios = portfolios.map(p => 
        p.id === editingPortfolio.id ? { ...portfolioData, id: editingPortfolio.id } : p
      );
      savePortfolios(newPortfolios);
    } else {
      // Ajout
      const newPortfolio = {
        ...portfolioData,
        id: Date.now() // Simple ID generation
      };
      savePortfolios([...portfolios, newPortfolio]);
    }
    setShowForm(false);
    setEditingPortfolio(null);
  };

  const categories = [
    { value: 'siteweb', label: t('portfolio.categories.siteweb') },
    { value: 'identitevisuelle', label: t('portfolio.categories.identitevisuelle') },
    { value: 'logo', label: t('portfolio.categories.logo') },
    { value: 'illustration', label: t('portfolio.categories.illustration') },
    { value: 'application', label: t('portfolio.categories.application') },
    { value: 'dessin', label: t('portfolio.categories.dessin') }
  ];

  const getCategoryLabel = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  return (
    <div className="portfolio-manager">
      <div className="portfolio-header">
        <h2>{t('admin.portfolio.title')}</h2>
        <button 
          onClick={handleAdd}
          className="add-portfolio-button"
        >
          {t('admin.portfolio.add')}
        </button>
      </div>

      {showForm && (
        <div className="form-overlay">
          <PortfolioForm
            portfolio={editingPortfolio}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setEditingPortfolio(null);
            }}
            categories={categories}
          />
        </div>
      )}

      <div className="portfolio-list">
        {portfolios.map(portfolio => (
          <div key={portfolio.id} className="portfolio-item">
            <img 
              src={portfolio.image} 
              alt={portfolio.name}
              className="portfolio-image"
              onError={(e) => {
                e.target.src = '/images/realisation-1.png'; // Image par défaut
              }}
            />
            <div className="portfolio-info">
              <h3>{portfolio.name}</h3>
              <p className="portfolio-description">{portfolio.description}</p>
              <div className="portfolio-meta">
                <span className="category">{getCategoryLabel(portfolio.category)}</span>
                <span className="date">{portfolio.date}</span>
              </div>
              {portfolio.client && (
                <div className="portfolio-client">
                  <strong>Client:</strong> {portfolio.client}
                </div>
              )}
              {portfolio.technologies && portfolio.technologies.length > 0 && (
                <div className="portfolio-technologies">
                  <strong>Technologies:</strong>
                  <div className="tech-tags">
                    {portfolio.technologies.filter(tech => tech.trim()).map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="portfolio-actions">
                <button 
                  onClick={() => handleEdit(portfolio)}
                  className="edit-button"
                >
                  {t('common.edit')}
                </button>
                <button 
                  onClick={() => handleDelete(portfolio.id)}
                  className="delete-button"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {portfolios.length === 0 && (
        <div className="empty-state">
          <p>{t('portfolio.noResults')}</p>
          <button onClick={handleAdd} className="add-portfolio-button">
            {t('admin.portfolio.add')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PortfolioManager;
